// Promo landing pages for passport services, served at /product/:slug.
// Slugs MUST match the exact URLs used in ad campaigns (case-sensitive).
const LOA = { name: "TPV Letter of Authorization", url: "https://irp.cdn-website.com/de603881/files/uploaded/TPV+LOA.pdf" };
const DS11 = { name: "DS-11 Application — complete online", url: "https://pptform.state.gov/" };
const DS82 = { name: "DS-82 Renewal Application — complete online", url: "https://pptform.state.gov/" };
const DS5504 = { name: "DS-5504 Application — complete online", url: "https://pptform.state.gov/" };
const DS64 = { name: "DS-64 — Lost or Stolen Passport Statement", url: "https://irp.cdn-website.com/de603881/files/uploaded/ds64_pdf.pdf" };
const DS3053 = { name: "DS-3053 — Statement of Consent", url: "https://irp.cdn-website.com/de603881/files/uploaded/ds3053.pdf" };
const SECOND_STMT = { name: "Second Passport Statement", url: "https://irp.cdn-website.com/de603881/files/uploaded/second_passport_statement.pdf" };

const SHIP = {
  method: "FedEx Priority Overnight",
  company: "UPV Services",
  recipient: "Passport Department",
  address: "1629 K St NW, Suite 300, Washington, DC 20006",
  phone: "(866) 334-8472",
};

export const PASSPORT_PRODUCTS = {
  "Get-a-New-Passport": {
    label: "Get a New Passport",
    overline: "Passport Services",
    h1: "Get a New U.S. Passport",
    metaTitle: "Get a New U.S. Passport — Expedited First-Time Passport | UPV Services",
    metaDescription: "Applying for a new U.S. passport? Follow UPV Services' step-by-step guide — required documents, DS-11, acceptance agent, and expedited processing. Call 866-334-8472.",
    intro: "Applying for your first U.S. passport? Follow the two steps below. Gather your materials, get your DS-11 authorized by an Acceptance Agent, then ship everything to UPV Services for expedited processing.",
    relatedSlug: "first-time-passport",
    step1: {
      heading: "Step 1: Gather U.S. Passport Application Materials",
      items: [
        { title: "Letter of Authorization (required)", desc: "Complete and sign the TPV Letter of Authorization form.", form: LOA },
        { title: "Two Passport Photos", desc: "2×2 inches, in color, on a plain light or white background, showing a full front view of the face." },
        { title: "Proof of U.S. Citizenship", desc: "An original or certified copy — no photocopies or notarized copies. Your original is returned with your completed passport. Provide one of: Official Birth Certificate (if born in the U.S.), most recent passport, Naturalization Certificate, or Certificate of Citizenship (if born abroad to U.S. parents)." },
        { title: "Proof of Identity", desc: "A Driver's License or State ID issued more than 6 months ago. Include a clear photocopy of the front and back." },
        { title: "If your ID was issued less than 6 months ago", desc: "Submit an alternative form of identification and a supplemental form. Applicants age 16 or 17 without a valid, 6-month-old license may have a parent co-sign — include a birth certificate proving the parental relationship." },
        { title: "Travel Evidence", desc: "Two copies of one of the following: a plane ticket showing foreign departure, a travel itinerary (with applicant name, travel date, and flight info), or a business travel letter." },
        { title: "DS-11 Government Application", desc: "Complete the DS-11 online. Do NOT sign it yet — you'll sign in front of the Acceptance Agent. Print TWO copies and leave ONE copy on the OUTSIDE of the sealed envelope.", form: DS11 },
      ],
      fee: "Government Fee: $216.87, payable to the U.S. Department of State.",
    },
    step2: {
      heading: "Step 2: Get Your Application Authorized by an Acceptance Agent",
      authorize: {
        intro: "First-time and new applicants must have their application authorized by a local Acceptance Agent (AA). AAs are located in Post Offices and Courthouses — not every location has one, and hours vary. The AA witnesses your signature, checks your ID, and seals part of your application.",
        sealed: [
          "The first signed copy of the DS-11",
          "The original proof of citizenship",
          "One passport photo",
          "The driver's license photocopy (or ID supplement)",
          "The travel evidence",
          "One copy of the Letter of Authorization",
          'The check to "U.S. Department of State"',
        ],
        unsealed: [
          "The second signed copy of the DS-11",
          "The second copy of the Letter of Authorization",
          "One passport photo",
          "The UPV Services order confirmation",
          "The UPV Services payment check (if not paid online)",
        ],
        notes: [
          "If the AA seals an item that was meant to stay unsealed, simply print an extra copy so you can include the required loose copies in your package.",
          "Ensure the AA writes the applicant's name, departure date, and date of birth on the outside of the sealed envelope. For multiple applicants, all names must appear on the envelope.",
          "Tell the AA you're using UPV Services to expedite — they must return the sealed envelope to you. If needed, refer them to the Department of State's Hand Carrying of Executed Applications policy (page 31 or 44 of their manual) or call UPV Services for assistance.",
        ],
      },
      ship: SHIP,
    },
  },

  "Renew-My-Passport": {
    label: "Renew My Passport",
    overline: "Passport Services",
    h1: "Renew My U.S. Passport",
    metaTitle: "Renew My U.S. Passport — Expedited DS-82 Renewal | UPV Services",
    metaDescription: "Renew your U.S. passport fast. UPV Services' step-by-step DS-82 renewal guide covers required documents and expedited processing. Call 866-334-8472.",
    intro: "Renewing an existing passport is simpler — no Acceptance Agent visit required. Gather the materials below, complete your DS-82, and ship everything to UPV Services for expedited processing.",
    relatedSlug: "passport-renewal-washington-dc",
    step1: {
      heading: "Step 1: Gather U.S. Passport Application Materials",
      items: [
        { title: "Letter of Authorization (required)", desc: "Complete and sign the TPV Letter of Authorization form.", form: LOA },
        { title: "Two Passport Photos", desc: "2×2 inches, in color, on a plain light or white background, showing a full front view of the face." },
        { title: "Most Recent Passport", desc: "Include your current, undamaged passport." },
        { title: "Official Name-Change Documentation (if applicable)", desc: "If your name differs from your current passport, provide a marriage certificate, divorce decree, or court order bearing the official seal of the issuing authority. Uncertified or notarized copies are not accepted; all documents are returned with your passport." },
        { title: "Letter Requesting Early Renewal (if applicable)", desc: "A letter to the U.S. Department of State explaining why you need an early renewal — e.g. dramatic changes in appearance, extended trips abroad, or other urgent travel needs." },
        { title: "Travel Evidence", desc: "Two copies of one of the following: a plane ticket showing foreign departure, a travel itinerary (with your name, travel date, and flight info), or a business travel letter." },
        { title: "DS-82 Government Renewal Application", desc: "Complete the DS-82 online, then print and sign it.", form: DS82 },
      ],
      fee: "Government Fee: $216.87, payable to the U.S. Department of State.",
    },
    step2: { heading: "Step 2: Ship Materials to UPV Services", ship: SHIP },
  },

  "Second-Passport": {
    label: "Second Passport",
    overline: "Passport Services",
    h1: "Get a Second U.S. Passport",
    metaTitle: "Second U.S. Passport — Expedited Application | UPV Services",
    metaDescription: "Frequent traveler needing a second U.S. passport? Follow UPV Services' step-by-step guide and ship your materials for expedited processing. Call 866-334-8472.",
    intro: "A second, limited-validity U.S. passport helps frequent travelers who can't be without their book. Gather the materials below and ship everything to UPV Services for expedited processing.",
    relatedSlug: "second-passport",
    step1: {
      heading: "Step 1: Gather U.S. Passport Application Materials",
      items: [
        { title: "Letter of Authorization (required)", desc: "Complete and sign the TPV Letter of Authorization form.", form: LOA },
        { title: "Two Passport Photos", desc: "Two recent 2×2 inch color photos on a plain light or white background." },
        { title: "Ten-Year Valid Passport", desc: "Include your current, valid, and signed U.S. passport." },
        { title: "Letter Requesting a Second Passport", desc: "A letter to the U.S. Department of State explaining the specific reason you need a second passport (e.g. frequent international travel, visa-processing conflicts, or countries with conflicting visa policies).", form: SECOND_STMT },
        { title: "Travel Evidence", desc: "Two copies of one of the following: a plane ticket showing foreign departure, a travel itinerary (with applicant name, travel dates, and flight details), or a business travel letter." },
        { title: "DS-82 Government Application", desc: "Complete the DS-82 online, then print and sign it.", form: DS82 },
      ],
      fee: "Government Fee: $216.87, payable to the U.S. Department of State.",
    },
    step2: { heading: "Step 2: Ship Materials to UPV Services", ship: SHIP },
  },

  "Lost-or-Stolen-Passport": {
    label: "Lost or Stolen Passport",
    overline: "Passport Services",
    h1: "Replace a Lost or Stolen U.S. Passport",
    metaTitle: "Lost or Stolen U.S. Passport Replacement — Expedited | UPV Services",
    metaDescription: "Lost or stolen passport? Follow UPV Services' step-by-step replacement guide — DS-64, DS-11, acceptance agent, and expedited processing. Call 866-334-8472.",
    intro: "A lost or stolen passport must be reported before it can be replaced. Gather the materials below, get your DS-11 authorized by an Acceptance Agent, then ship everything to UPV Services for expedited processing.",
    relatedSlug: "lost-passport-replacement",
    step1: {
      heading: "Step 1: Gather U.S. Passport Application Materials",
      items: [
        { title: "Letter of Authorization (required)", desc: "Complete and sign the TPV Letter of Authorization form.", form: LOA },
        { title: "Two Passport Photos", desc: "2×2 inch color photos on a plain light or white background." },
        { title: "Proof of U.S. Citizenship", desc: "An original or certified copy of one of: Official Birth Certificate (if born in the U.S.), most recent passport, Naturalization Certificate, or Certificate of Citizenship (if born abroad to U.S. parents)." },
        { title: "Proof of Identity", desc: "A clear photocopy of a Driver's License or State ID issued more than 6 months ago. If issued less than 6 months ago, provide an alternative form of identification and a supplemental form. Applicants age 16 or 17 without a valid 6-month-old license require a parental co-signature and a birth certificate proving the relationship." },
        { title: "Travel Evidence", desc: "Two copies of one of the following: a plane ticket showing foreign departure, a travel itinerary (with applicant name, travel date, and flight info), or a business travel letter." },
        { title: "DS-64 Statement (if necessary)", desc: "Complete the DS-64 statement regarding your lost or stolen passport.", form: DS64 },
        { title: "DS-11 Government Application", desc: "Complete the DS-11 online. Do NOT sign it yet — you'll sign in front of the Acceptance Agent.", form: DS11 },
      ],
      fee: "Government Fee: $216.87, payable to the U.S. Department of State.",
    },
    step2: {
      heading: "Step 2: Get Your Application Authorized by an Acceptance Agent",
      authorize: {
        intro: "Lost/stolen and new applicants must have their application authorized by a local Acceptance Agent (AA). AAs are located in Post Offices and Courthouses — not every location has one, and hours vary. The AA witnesses your signature, checks your ID, and seals part of your application.",
        sealed: [
          "The first signed copy of the DS-11",
          "The original proof of citizenship",
          "The two passport photos",
          "The driver's license photocopy (or supplemental ID)",
          "The travel evidence",
          "One copy of the Letter of Authorization",
          'The check to "U.S. Department of State"',
        ],
        unsealed: [
          "The second signed copy of the DS-11",
          "The second copy of the Letter of Authorization",
          "The UPV Services order confirmation page",
          "The UPV Services payment check (if not paid by credit card at checkout)",
        ],
        notes: [
          "The AA will occasionally insist on sealing an item meant to stay unsealed — that's fine. Just print another copy so you can include the required loose copies in your package.",
          "The AA should write the applicant's name, departure date, and date of birth on the outside of the sealed envelope. If two applications share one envelope, both applicants' information must appear.",
          "Tell the AA you're using UPV Services to expedite — they must return the envelope to you. If needed, refer them to the Department of State's Hand Carrying of Executed Applications policy (page 31 or 44 of their manual), or call UPV Services for assistance.",
        ],
      },
      ship: SHIP,
    },
  },

  "Passport-Name-Change": {
    label: "Passport Name Change",
    overline: "Passport Services",
    h1: "U.S. Passport Name Change",
    metaTitle: "U.S. Passport Name Change — Expedited DS-5504 | UPV Services",
    metaDescription: "Update the name on your U.S. passport. UPV Services' step-by-step DS-5504 name-change guide covers documents and expedited processing. Call 866-334-8472.",
    intro: "Recently married, divorced, or corrected your name? Gather the materials below, complete your DS-5504, and ship everything to UPV Services for expedited processing.",
    relatedSlug: "name-change-passport",
    step1: {
      heading: "Step 1: Gather U.S. Passport Application Materials",
      items: [
        { title: "Letter of Authorization (required)", desc: "Complete and sign the TPV Letter of Authorization form.", form: LOA },
        { title: "Two Passport Photos", desc: "2×2 inch color photos on a plain light or white background." },
        { title: "Valid U.S. Passport", desc: "Issued within the last year. If your passport was issued more than one year ago, use the passport renewal instructions instead." },
        { title: "Official Name-Change Documentation", desc: "A marriage certificate, divorce decree, or court order showing the name change, bearing the official seal of the issuing authority. Uncertified or notarized copies cannot be accepted; all documents are returned with your passport." },
        { title: "Travel Evidence", desc: "Two copies of one of the following: a plane ticket showing foreign departure, a travel itinerary (with applicant name, travel date, and flight info), or a business travel letter." },
        { title: "DS-5504 Government Application", desc: "Complete the DS-5504 online.", form: DS5504 },
      ],
      fee: "Government Fee: $216.87, payable to the U.S. Department of State.",
    },
    step2: { heading: "Step 2: Ship Materials to UPV Services", ship: { ...SHIP, method: "FedEx Priority Overnight (for Emergency Services only)" } },
  },

  "Child-Under-Age-16": {
    label: "Child Under Age 16",
    overline: "Passport Services",
    h1: "Child Passport (Under Age 16)",
    metaTitle: "Child Passport Under Age 16 — Expedited Minor Passport | UPV Services",
    metaDescription: "Getting a U.S. passport for a child under 16? Follow UPV Services' step-by-step guide — DS-11, DS-3053 consent, both-parent rules, and expedited processing. Call 866-334-8472.",
    intro: "Passports for children under 16 have special rules, including consent from both parents or guardians. Gather the materials below, appear with your child before an Acceptance Agent, then ship everything to UPV Services for expedited processing.",
    relatedSlug: "child-passport",
    step1: {
      heading: "Step 1: Gather Required Documents",
      items: [
        { title: "Letter of Authorization", desc: "Complete and sign the UPV Letter of Authorization form.", form: LOA },
        { title: "Passport Photos", desc: "Two color photos, 2×2 inches, on a white or light background — full front view, no glasses or headwear." },
        { title: "Proof of U.S. Citizenship", desc: "One of the following: Official Birth Certificate, most recent passport, Naturalization Certificate, or Certificate of Citizenship (if born abroad)." },
        { title: "Proof of Relationship to Child", desc: "One of the following: birth certificate showing both parents' names, foreign birth certificate (with translation), FS-240 or DS-1350, adoption decree, or a court order establishing custody or guardianship." },
        { title: "Statement of Consent — DS-3053 (if one parent can't appear)", desc: "The non-appearing parent must sign and notarize the DS-3053 form and include a copy of their valid ID (driver's license or passport).", form: DS3053 },
        { title: "Proof of Parents' Identity", desc: "A valid Driver's License, Passport, Military, or Government ID. If the ID was issued within the last 6 months, bring a second form of ID." },
        { title: "Travel Evidence", desc: "Two copies of one of the following: a plane ticket or travel itinerary showing the applicant's name and travel dates, or a business travel letter (if applicable)." },
        { title: "DS-11 Government Form", desc: "Complete online (do not sign until in front of the Acceptance Agent). Print two copies — one for the sealed envelope, one for the outside.", form: DS11 },
      ],
      fee: "Government Fee: Check for $186.87, payable to the U.S. Department of State.",
    },
    step2: {
      heading: "Step 2: Get the Application Authorized",
      authorize: {
        intro: "Both parents (or guardians) and the child must appear in person before a Passport Acceptance Agent (AA). AAs are located at post offices and courthouses nationwide. The AA witnesses the signature on the DS-11, verifies ID, and seals the required items into an official envelope.",
        sealed: [
          "Signed DS-11 form",
          "Proof of citizenship and relationship",
          "DS-3053 (if applicable)",
          "Two passport photos",
          "Copy of ID",
          "Travel evidence",
          "One Letter of Authorization",
          "Check for $186.87 (U.S. Dept. of State)",
        ],
        unsealed: [
          "Second copy of the DS-11",
          "Second Letter of Authorization",
          "UPV Services order confirmation",
          "Check for UPV Services (if not paid online)",
        ],
        notes: [
          "If the AA tries to seal items meant to stay unsealed, provide duplicates.",
          "Ask the AA to write the applicant's name, date of birth, and travel date on the envelope.",
          "Inform them UPV Services will expedite the application. If they refuse to release the sealed envelope, refer to the Department of State's Hand Carrying of Executed Applications policy, or call UPV Services for assistance.",
        ],
      },
      ship: { ...SHIP, method: "FedEx Priority Overnight (for emergency services only)" },
    },
  },
};
export const PASSPORT_PRODUCT_LIST = [
  "Get-a-New-Passport",
  "Renew-My-Passport",
  "Second-Passport",
  "Child-Under-Age-16",
  "Lost-or-Stolen-Passport",
  "Passport-Name-Change",
].map((slug) => ({ slug, label: PASSPORT_PRODUCTS[slug].label }));
